import axios from "axios";

class CacheStorageService {
    constructor(timelimit) {
        this.cacheName = "eticket-storage"
        this.timeLimit = timelimit || 10 // minutes
    }

    /**
     * @author Adriana Fernandez
     * @description Either returns data from cache or sends a GET request
     * @date 17-10-2023
     * @param {URL} url
     * @param {String} token
     * @returns {Object}
     * @memberof CacheStorageService
     */
    fetchData = async (url, token) => {
        const cacheAvailable = 'caches' in window;

        if (cacheAvailable) {
            let cachedData = await this.getCachedData(url);
            if (cachedData) {
                cachedData.data.isCached = true
                return cachedData
            }
        }

        let response = await axios.get(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (cacheAvailable && response && response.status === 200 && response.data && response.data.statusCode === 200) {
            this.saveToCache(url, response)
        }
        return response;
    }

    /**
     * @author Adriana Fernandez
     * @description Retrieves data from cache
     * @date 17-10-2023
     * @param {URL} url
     * @returns {Object}
     * @memberof CacheStorageService
     */
    getCachedData = async (url) => {
        const cacheAvailable = 'caches' in window;
        if (!cacheAvailable) return false;

        const cacheStorage = await caches.open(this.cacheName);
        const cachedResponse = await cacheStorage.match(url);

        if (!cachedResponse || !cachedResponse.ok) {
            return false;
        }

        const data = await cachedResponse.json();
        const minuteDifference = Math.round((Date.now() - data.cachedTime) / 60000);

        if (minuteDifference > this.timeLimit) {
            await this.deleteItemFromCache(url)
            return false
        }

        return data;
    }

    /**
     * @author Adriana Fernandez
     * @description Delete an item from cache
     * @date 17-10-2023
     * @param {URL} url
     * @returns {Object}
     * @memberof CacheStorageService
     */
    deleteItemFromCache = async (url) => {
        const cacheAvailable = 'caches' in window;
        if (!cacheAvailable) return;

        const cacheStorage = await caches.open(this.cacheName);
        cacheStorage.delete(url)
    }

    /**
     * @author Adriana Fernandez
     * @description Deletes items from cache whose key contains a url
     * @date 18-10-2023
     * @param {URL} url
     * @returns {Object}
     * @memberof CacheStorageService
     */
    deleteUrlFromCache = async (url) => {
        const cacheAvailable = 'caches' in window;
        if (!cacheAvailable) return;

        const cacheStorage = await caches.open(this.cacheName);
        const allKeys = await cacheStorage.keys();

        for (const key of allKeys) {
            if (key.url.includes(url)) {
                cacheStorage.delete(key);
            }
        }
    }

    /**
     * @author Adriana Fernandez
     * @description Save an item to cache
     * @date 17-10-2023
     * @param {URL} url
     * @param {Object} data
     * @returns {Object}
     * @memberof CacheStorageService
     */
    saveToCache = async (url, data) => {
        const cacheAvailable = 'caches' in window;
        if (!cacheAvailable) return;

        const cacheStorage = await caches.open(this.cacheName);
        let copy = { ...data, cachedTime: Date.now() }
        const storedText = JSON.stringify(copy);
        await cacheStorage.put(url, new Response(storedText));
    }

    /**
     * @author Adriana Fernandez
     * @description Clear all cache
     * @date 17-10-2023
     * @returns {Object}
     * @memberof CacheStorageService
     */
    clearCache = async () => {
        const cacheAvailable = 'caches' in window;
        if (!cacheAvailable) return;

        await caches.delete(this.cacheName)
    }
}

export { CacheStorageService as default };